export const OFFER_PLACEMENTS = {
  DEFAULT_LANDING: "landing.default",
  CONFIRMATION_LANDING: "landing.konfirmation",
  CHRISTENING_LANDING: "landing.barnedab",
  BIRTHDAY_LANDING: "landing.fodselsdag",
  BAR_MITZVAH_LANDING: "landing.bar-mitsva",
  CHRISTMAS_LANDING: "landing.julefrokost",
  BLOG: "landing.blog",
  PRINTED_FOR_YOU: "landing.printed-for-you",
  DASHBOARD_PURCHASE: "dashboard.purchase",
  DASHBOARD_EVENT: "dashboard.event",
} as const;

export const PRINT_BENEFITS = {
  THANK_YOU_CARDS: "print.thank_you_cards",
  TABLE_CARDS: "print.table_cards",
  /** A plan purchase's photo book (src/lib/photobook/includedBook.ts). */
  PHOTO_BOOK: "print.photo_book",
} as const;

export type PlanPackageType = "basic" | "premium";

export function normalizeOfferLocale(locale: string): string {
  const [language = "en", region] = locale.trim().replace("_", "-").split("-");
  return region
    ? `${language.toLowerCase()}-${region.toUpperCase()}`
    : language.toLowerCase();
}

export type LocalizedOfferPresentation = Record<
  string,
  {
    badge?: string;
    headline?: string;
    description?: string;
  }
>;

export interface ActiveOfferBenefit {
  id: string;
  benefitCode: string;
  quantity: number;
  redemptionMode: "deferred" | "preconfigured";
  includesShipping: boolean;
  expiresAfterDays: number | null;
  coverage: Record<string, unknown>;
  presentation: LocalizedOfferPresentation;
  position: number;
}

export interface ActiveOffer {
  id: string;
  code: string;
  name: string;
  packageType: PlanPackageType;
  marketCode: string | null;
  currency: string;
  stripePriceId: string | null;
  priceMinor: number | null;
  startsAt: string;
  endsAt: string | null;
  termsVersion: string;
  stackingPolicy: "exclusive" | "stackable";
  presentation: LocalizedOfferPresentation;
  benefits: ActiveOfferBenefit[];
}

export interface PublicOfferBenefit {
  benefitCode: string;
  quantity: number;
  label: string | null;
}

export interface PublicOffer {
  code: string;
  packageType: PlanPackageType;
  currency: string;
  priceMinor: number | null;
  badge: string | null;
  headline: string | null;
  description: string | null;
  benefits: PublicOfferBenefit[];
}

export function marketCodeForLocale(locale: string): string | null {
  const normalized = locale.toLowerCase().split(/[-_]/)[0];
  const markets: Record<string, string> = {
    da: "DK",
    de: "DE",
    el: "GR",
    es: "ES",
    fi: "FI",
    fo: "FO",
    fr: "FR",
    is: "IS",
    it: "IT",
    nl: "NL",
    no: "NO",
    pt: "PT",
    sv: "SE",
    en: "US",
  };
  return markets[normalized] ?? null;
}

export function currencyForLocale(locale: string): string {
  const normalized = locale.toLowerCase().split(/[-_]/)[0];
  const currencies: Record<string, string> = {
    da: "DKK",
    de: "EUR",
    el: "EUR",
    es: "EUR",
    fi: "EUR",
    fo: "DKK",
    fr: "EUR",
    is: "ISK",
    it: "EUR",
    nl: "EUR",
    no: "NOK",
    pt: "EUR",
    sv: "SEK",
    en: "USD",
  };
  return currencies[normalized] ?? "USD";
}

export function marketCodeForOfferContext(
  locale: string,
  currency: string,
): string | null {
  const localeMarket = marketCodeForLocale(locale);
  const normalizedCurrency = currency.toUpperCase();
  // English pages charge visitors from Denmark, Sweden and Norway in their own
  // currency (englishPagePricing), so the currency names their market.
  if (localeMarket === "US" && normalizedCurrency === "DKK") return "DK";
  if (localeMarket === "US" && normalizedCurrency === "SEK") return "SE";
  if (localeMarket === "US" && normalizedCurrency === "NOK") return "NO";
  if (localeMarket === "US" && normalizedCurrency === "EUR") return null;
  return localeMarket;
}

function localizedPresentation(
  presentation: LocalizedOfferPresentation,
  locale: string,
) {
  const exactLocale = normalizeOfferLocale(locale);
  const normalized = exactLocale.split("-")[0];
  return (
    presentation[exactLocale] ??
    presentation[normalized] ??
    presentation.default ??
    {}
  );
}

export function toPublicOffer(offer: ActiveOffer, locale: string): PublicOffer {
  const copy = localizedPresentation(offer.presentation, locale);
  return {
    code: offer.code,
    packageType: offer.packageType,
    currency: offer.currency,
    priceMinor: offer.stripePriceId ? offer.priceMinor : null,
    badge: copy.badge ?? null,
    headline: copy.headline ?? null,
    description: copy.description ?? null,
    benefits: offer.benefits.map((benefit) => ({
      benefitCode: benefit.benefitCode,
      quantity: benefit.quantity,
      label:
        localizedPresentation(benefit.presentation, locale).headline ?? null,
    })),
  };
}

export function formatOfferPrice(
  offer: PublicOffer,
  locale: string,
): string | null {
  if (offer.priceMinor === null) return null;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: offer.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(offer.priceMinor / 100);
}
